import type { ReactNode } from 'react';
import { FieldError, Label, Text } from 'react-aria-components';

/** Shared, explicit base styles: nothing here relies on Tailwind preflight. */
export const labelClass = 'block m-0 p-0 font-ui text-sm font-semibold text-ink';
export const hintClass = 'block m-0 p-0 font-ui text-xs text-muted';
export const errorClass = 'block m-0 p-0 font-ui text-xs text-danger';
export const fieldClass = 'flex flex-col gap-1 m-0 p-0 border-0 bg-transparent font-ui';
// 16px on touch screens so iOS Safari doesn't zoom the page when a field gets focus.
export const controlClass =
  'm-0 px-2 py-1 touch:py-2 font-ui text-md touch:text-[16px] leading-control text-ink bg-surface border border-line-strong rounded-control appearance-none outline-none [&:focus-visible]:outline [&:focus-visible]:outline-solid [&:focus-visible]:outline-2 [&:focus-visible]:outline-offset-2 [&:focus-visible]:outline-ink data-[focus-visible]:outline data-[focus-visible]:outline-solid data-[focus-visible]:outline-2 data-[invalid]:border-danger';

/** Row layout shared by Checkbox, Switch and Radio. */
export const rowClass = 'flex items-center gap-2 m-0 p-0 touch:min-h-[44px] border-0 bg-transparent font-ui text-md text-ink cursor-pointer';

/** Joins class parts with single spaces, dropping empty ones. */
export function joinClasses(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

export function fieldClassName(extra?: string): string {
  return joinClasses(fieldClass, extra);
}

/** Visible `n / max` counter; pass its id to the control's aria-describedby. */
export function Counter({ id, length, max }: { id: string; length: number; max: number }) {
  return (
    <span id={id} className={hintClass}>
      {length} / {max}
    </span>
  );
}

export function FieldLabel({ children, hidden }: { children: ReactNode; hidden?: boolean }) {
  return <Label className={hidden ? joinClasses(labelClass, 'sr-only') : labelClass}>{children}</Label>;
}

/**
 * With `id`, a plain span the control references itself through
 * aria-describedby: react-aria links its description slot only if the hint is
 * there at mount, so a hint that appears later would go unlinked.
 */
export function FieldHint({ hint, id }: { hint?: string; id?: string }) {
  if (hint && id) {
    return (
      <span id={id} className={hintClass}>
        {hint}
      </span>
    );
  }
  return hint ? (
    <Text slot="description" className={hintClass}>
      {hint}
    </Text>
  ) : null;
}

export function FieldErrorText({ error }: { error?: string }) {
  return <FieldError className={errorClass}>{error}</FieldError>;
}
